<template>
  <div class="track-zoom">
    <div class="track-zoom__toolbox">
      <div class="slider">
        <div class="triangle"></div>
        <cling-slider :min="1" :max="ratioMax" :value="ratio" @input="onInput"></cling-slider>
      </div>
    </div>
    <div class="track-zoom__ruler" ref="ruler" @wheel.shift.stop="onWheel" @click="setPlayHead">
      <!-- Time marks -->
      <div class="time-container">
        <div v-if="(videoDuration > 120)" class="minute-container">
          <div v-for="minute in minutes" :key="minute" class="minute" :style="minuteStyle">
            <div v-if="(minute % 5 === 0) && minute !=0">
              {{minute}}m
            </div>
          </div>
        </div>
        <!---->
        <div v-else class="second-container">
          <div v-for="second in seconds" :key="second"  class="second" :style="secondStyle">
            <div v-if="(second % 10 === 0) && (videoDuration <= 60)">
              {{second}}s
            </div>
            <div v-else-if="(second % 15 === 0)">
              {{second}}
            </div>
          </div>
        </div>
      </div>
      <!-- Zoom Box -->
      <div class="track-zoom__box" :class="{'track-zoom__box-moveable': this.ratio !=1}" ref="zoomBox" :style="boxStyle" @mousedown.stop="onMouseDown">
        <div v-if="this.ratio !=1" class="zoom-button" @click="resetZoom">
          <i v-tooltip.bottom-center="'Zoom out'" class="material-icons" >zoom_out_map</i>
        </div>
        <div v-if="selection.scene" class="zoom-button" @click="zoomToSelection">
          <i v-tooltip.bottom-center="'Zoom to selection'" class="material-icons" >zoom_in</i>
        </div>
      </div>
      <!-- Current time mark-->
      <drag-handle class="track-zoom__tracks-progress" :style="timePosition" v-on:dragging="setPlayHead">
        &nbsp;
      </drag-handle>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import dragHandle from '../tagHandle.vue'
import clingSlider from '../../core/clingSlider'

const sceneStartTime = scene => Math.min(...scene.waypoints.map(w => w.time))
const sceneEndTime = scene => Math.max(...scene.waypoints.map(w => w.time))

export default {
  name: 'trackZoom',

  components: {
    'drag-handle': dragHandle,
    'cling-slider': clingSlider
  },

  data: function () {
    return {
      ratio: 1,
      center: 0,
      left: 0,
      boxMoving: false
    }
  },

  computed: {
    ...mapState(['selection']),
    ...mapGetters(['videoDuration', 'currentTime']),

    ratioMax: function () {
      return this.videoDuration < 600 ? this.videoDuration / 10 : this.videoDuration / 20;
    },

    boxWidth: function () {
      return 1 / this.ratio;
    },

    computedLeft: {
      get: function () {
        return this.left;
      },

      set: function (newValue) {
        if (this.left + newValue >= 0 && this.left + newValue + this.boxWidth <= 1) {
          this.left += newValue;
        } else if (this.left + newValue < 0) {
          this.left = 0;
        } else if (this.left + newValue + this.boxWidth > 1) {
          this.left = 1 - this.boxWidth;
        }
      }
    },

    minutes: function () {
      let minutes = [];
      let i = 0;
      for (i; i <= Math.floor(this.videoDuration / 60); i++) {
        minutes.push(i);
      }
      return minutes;
    },

    seconds: function () {
      let seconds = [];
      let i = 0;
      for (i; i <= this.videoDuration; i++) {
        seconds.push(i);
      }
      return seconds;
    },

    minuteStyle: function () {
      let minuteWidth = 100 / (this.videoDuration / 60);
      return {
        width: minuteWidth + '%'
      };
    },

    secondStyle: function () {
      let secondWidth = 100 / (this.videoDuration);
      return {
        width: secondWidth + '%'
      };
    },

    boxStyle: function () {
      return {
        left: this.computedLeft * 100 + '%',
        width: 100 / this.ratio + '%'
      };
    },

    timePosition: function () {
      return {
        left: `${this.videoDuration ? this.currentTime * 100 / this.videoDuration : 0}%`
      };
    }
  },

  created() {
    this.keyEventUnsubscribe = this.keyEventSubscribe(['tracks-manager'], {
      'ArrowRight': this.onMoveRight,
      'ArrowLeft': this.onMoveLeft
    });
  },

  beforeDestroy() {
    this.keyEventUnsubscribe()
  },

  methods: {
    ...mapActions(['createTag']),
    ...mapMutations(['setPlayerTime']),

    zoomToSelection: function () {
      let selectionStart = sceneStartTime(this.selection.scene) - (this.videoDuration / 50);
      let selectionEnd = sceneEndTime(this.selection.scene) + (this.videoDuration / 50);
      let sceneDuration = selectionEnd - selectionStart;

      if (this.selection.scene) {
        this.ratio = this.videoDuration / sceneDuration <= this.ratioMax ? this.videoDuration / sceneDuration : this.ratioMax;
        this.left = selectionStart / this.videoDuration;

        this.emitZoomValues();
      }
    },

    setZoom: function (value) {
      if (value >= this.ratioMax) {
        value = this.ratioMax;
      } else if (value < 1) {
        value = 1;
      }

      let left = this.left;
      let oldWidth = 1 / this.ratio;
      let newWidth = 1 / value;
      let deltaWidth = newWidth - oldWidth;

      if (left + newWidth > 1) {
        left = 1 - newWidth;
      } else if ((left - (deltaWidth / 2)) < 0) {
        left = 0;
      } else if (left !== 0) {
        left -= (deltaWidth / 2);
      }

      this.left = left;
      this.ratio = value;

      this.emitZoomValues();
    },

    resetZoom: function () {
      this.ratio = 1;
      this.center = 0;
      this.left = 0;

      this.emitZoomValues();
    },

    leftMove: function (value) {
      this.computedLeft = value;

      this.emitZoomValues();
    },
    
    setPlayHead: function (e) {
      let boundingBox = this.$refs.ruler.getBoundingClientRect();
      let x = e.x || e.cx || 0
  
      const pos = (x - boundingBox.left) / (boundingBox.width)
      this.setPlayerTime(pos * this.videoDuration)
    },

    onInput: function (value) {
      this.setZoom(value);
    },

    onWheel: function (e) {
      this.setZoom(this.ratio + this.ratioMax / e.deltaY)
    },

    onMoveLeft: function () {
      this.leftMove(-0.01);
    },

    onMoveRight: function () {
      this.leftMove(0.01);
    },

    onMouseDown: function () {
      this.boxMoving = true;
      document.addEventListener('mousemove', this.onMouseMove);
      document.addEventListener('mouseup', this.onMouseUp);
    },

    onMouseMove: function (e) {
      e.stopImmediatePropagation();
      let boundingBox = this.$refs.ruler.getBoundingClientRect();

      if (this.boxMoving && this.ratio > 1) {
        this.leftMove(e.movementX / boundingBox.width);
      }
    },

    onMouseUp: function () {
      this.boxMoving = false;
      document.removeEventListener('mousemove', this.onMouseMove);
      document.removeEventListener('mouseup', this.onMouseUp);
    },

    emitZoomValues: function () {
      this.$emit('zoomUpdated', {ratio: this.ratio, center: this.center, left: this.computedLeft});
    }
  }
}
</script>
